import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import Tooltip from '@components/Tooltip';
import styled from '@emotion/styled';
import useToggleFavoritePrepaidCard from '@hooks/prepaidCard/useToggleFavoritePrepaidCard';
import useToast from '@hooks/ui/useToast';
import FavoriteCircleFill from '@icons/favorite/FavoriteCircleFill';
import { FavoritePrepaidCardRow } from '@models/prepaidCard';
import { Link } from '@stackflow/link';
import { useFlow } from '@stackflow/react';
import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useRolling from './hooks/useRolling';
import PlusIcon from './PlusIcon';
import Remote from './Remote';
import styles from './styles.module.scss';

interface Props {
    cards: FavoritePrepaidCardRow[];
    isBlank: boolean;
}

function MultipleCard({ cards, isBlank }:Props) {
    const { t } = useTranslation('prepaidCard');
    const {
        getStyle, moveNext, movePrev, index,
    } = useRolling({ cards });
    const toast = useToast();

    const { handleToggleFavorite } = useToggleFavoritePrepaidCard({ id: cards?.[index]?.id, initialValue: cards?.[index]?.isFavorites || false });
    const handleHeartClick = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleToggleFavorite();
        toast.info(t('multipleCard.favoriteRemoved'));
    };
    const { push } = useFlow();
    const containerHeight = useMemo(() => {
        if (cards.length !== 0) {
            return cards.length * 30 + 328;
        }
        return 0;
    }, [cards]);
    return (
        <Container height={containerHeight}
            align='center'
            fullWidth>
            {!isBlank && (
                <Tooltip right='unset'
                    top={24}
                    visibleKey='prepaidCardLog'>
                    {t('multipleCard.tooltip')}
                </Tooltip>
            )}
            {cards.map((card, i) => (
                <div key={`card ${i}`}
                    className={styles.cardContaner}
                    style={getStyle(i)}
                    onClick={() => {
                        push('PrepaidCardLogActivity', { prepaidCardId: card.id });
                    }} >
                    <div className={styles.card}>
                        <Image src={card.imageUrl}
                            width={300}
                            height={200}
                            alt={t('multipleCard.imageAlt')}/>
                        {index === i && (
                            <div className={styles.heartWrapper}
                                onClick={handleHeartClick} >
                                <FavoriteCircleFill size={32}/>
                            </div>
                        )}
                    </div>
                </div>))}
            {cards.length > 1 && (
                <Remote moveNext={moveNext}
                    movePrev={movePrev} />
            )}
            {!isBlank && (
                <Link activityParams={{ type: 'BUY' }}
                    activityName='PrepaidCardImagesActivity'
                    className={styles.new}>
                    <div className={styles.newIcon}>
                        <PlusIcon/>
                    </div>
                    <Text size='lg' >{t('blank.buyNewCard')}</Text>
                </Link>
            )}
        </Container>
    );
}

const Container = styled(Column)<{height: number}>`
    background-color: var(--color-gray2);
    height: ${(props) => (props.height ? `${props.height}px` : 'unset')};
    max-height: ${(props) => (props.height ? `${props.height}px` : 'unset')};
    min-height: ${(props) => (props.height ? `${props.height}px` : 'unset')};
    position: relative;
`;

export default MultipleCard;
